import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SelectComponent } from '@kinomap-dev/ng-ui-lib/forms';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { WorkoutMode } from '@kinomap-dev/ng-ui-lib/core';
import { MediaBlockService } from '../../services/media-block-service';
import { CreateContentService } from '../../services/create-content.service';
import { VideoUploader } from '../video-uploader/video-uploader';
import { SectionsEditor } from '../sections-editor/sections-editor';
import { SelectField } from '../select-field/select-field';
import { UpperFirstLetterPipe } from '../../pipes/upper-first-letter-pipe';
import { SPORT_TYPES, SportType } from '../../types/sport.type';
import { SectionModelType } from '../../types/section.type';
import { formatTime } from '../../utils/duration.utils';
import { COACHING_WORKOUT_MODE_BY_SPORT } from '../../configs/sections-editor/coaching-workout-mode.config';

/**
 * Video + editeur d'intervalles d'un coaching, partage entre le bloc d'entrainement et sa modale plein ecran.
 * Le select du mode ne propose qu'un choix, celui du sport (`COACHING_WORKOUT_MODE_BY_SPORT`),
 * selectionne automatiquement au choix du sport.
 */
@Component({
  selector: 'app-coaching-intervals-editor',
  imports: [
    TranslateModule,
    SelectComponent,
    IconComponent,
    VideoUploader,
    SectionsEditor,
    SelectField,
    UpperFirstLetterPipe,
  ],
  templateUrl: './coaching-intervals-editor.html',
  styleUrl: './coaching-intervals-editor.scss',
  host: { '[class.fullscreen]': 'fullscreen()' },
})
export class CoachingIntervalsEditor {
  protected mediaBlockService: MediaBlockService = inject(MediaBlockService);
  private createContentService: CreateContentService = inject(CreateContentService);
  protected readonly sportTypes = SPORT_TYPES;

  readonly fullscreen: InputSignal<boolean> = input<boolean>(false);
  /** Formulaire du coaching (`trainingBlock`), pour afficher ses erreurs. */
  readonly trainingForm: InputSignal<FormGroup | undefined> = input<FormGroup>();

  protected sport: WritableSignal<SportType | undefined> = this.createContentService.coachingSport;
  protected workoutMode: WritableSignal<WorkoutMode | undefined> =
    this.createContentService.coachingWorkoutMode;
  protected sections: WritableSignal<SectionModelType[]> =
    this.createContentService.coachingSections;
  protected selectedSectionsIds: WritableSignal<number[]> =
    this.createContentService.coachingSelectedSectionsIds;

  /** Seul mode propose pour le sport courant. */
  protected workoutModes: Signal<WorkoutMode[] | undefined> = computed(() => {
    const sport: SportType | undefined = this.sport();
    return sport ? [COACHING_WORKOUT_MODE_BY_SPORT[sport]] : undefined;
  });

  protected disabledEditor: Signal<boolean> = computed(() => !this.sport() || !this.workoutMode());

  protected readonly formatTime = formatTime;

  changeSport(sport: string): void {
    this.sport.set(sport as SportType);
    this.createContentService.setSharedSport(sport as SportType);
    this.workoutMode.set(COACHING_WORKOUT_MODE_BY_SPORT[sport as SportType]);
    this.sections.set([]);
  }
}
